<script>
import bem from '@/utils/bem';
import parseModuleContent from '@/utils/parseModuleContent';
import { mapActions, mapState } from 'vuex';
import { CloseOutlined } from '@ant-design/icons-vue';
import CheckCircleIcon from '@/components/svg/CheckCircleIcon.vue';
import DropdownArrowIcon from '@/components/svg/DropdownArrowIcon.vue';
import ChevronDoubleRightIcon from '@/components/svg/ChevronDoubleRightIcon.vue';

const b = bem('gallery-select-module');

export default {
  components: {
    CloseOutlined,
    CheckCircleIcon,
    DropdownArrowIcon,
    ChevronDoubleRightIcon,
  },
  props: {
    moduleData: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: [String, Number],
      default: null,
    },
    blockIndex: {
      type: Number,
      default: 0,
    },
    blockBackground: {
      type: String,
      default: 'transparent',
    },
    companyColors: {
      type: Object,
      default: () => ({
        color1: '',
        color2: '',
        color3: '',
      }),
    },
  },
  emits: ['update', 'select', 'selection-change'],
  data() {
    return {
      selectedFolderIds: [],
      isDropdownOpen: false,
      initialized: false,
    };
  },
  computed: {
    ...mapState({
      folders: (state) => state.photoGallery.folders,
      foldersByIds: (state) => state.photoGallery.foldersByIds,
      currentCompany: (state) => state.companies.currentCompany,
    }),
    content() {
      return parseModuleContent(this.moduleData);
    },
    selectedFolders() {
      // Use foldersByIds (fetched with images) if available, otherwise fall back to folders list
      const source = this.foldersByIds.length > 0 ? this.foldersByIds : this.folders;
      return this.selectedFolderIds
        .map((id) => source.find((f) => f.id === id))
        .filter(Boolean);
    },
    getFolderImages() {
      return (folder) => folder.images || [];
    },
    blockType() {
      return this.content?.blockType || null;
    },
    showItemsBlock() {
      if (!this.blockType) return true;
      return this.blockType === 'items';
    },
  },
  watch: {
    content: {
      handler(newContent) {
        // Support both new folderIds and legacy galleryIds
        const ids = newContent?.folderIds || newContent?.galleryIds;
        if (ids && Array.isArray(ids)) {
          const newIds = JSON.stringify(ids);
          const oldIds = JSON.stringify(this.selectedFolderIds);
          if (newIds !== oldIds) {
            this.selectedFolderIds = [...ids];
          }
          if (!this.initialized) {
            this.initialized = true;
            this.$nextTick(() => {
              this.emitSelectionChange();
            });
          }
        }
      },
      immediate: true,
    },
    companyId: {
      handler(newCompanyId) {
        if (newCompanyId) {
          this.getFoldersAction(newCompanyId);
        }
      },
      immediate: true,
    },
    blockIndex: {
      handler() {
        if (this.selectedFolderIds.length > 0) {
          this.emitSelectionChange();
        }
      },
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getFoldersAction: 'photoGallery/getFolders',
      getFoldersByIdsAction: 'photoGallery/getFoldersByIds',
    }),
    loadSelectedFolders() {
      if (this.selectedFolderIds.length > 0) {
        this.getFoldersByIdsAction(this.selectedFolderIds);
      }
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    isSelected(folderId) {
      return this.selectedFolderIds.includes(folderId);
    },
    toggleFolder(folder) {
      const index = this.selectedFolderIds.indexOf(folder.id);
      if (index === -1) {
        this.selectedFolderIds.push(folder.id);
      } else {
        this.selectedFolderIds.splice(index, 1);
      }
      this.loadSelectedFolders();
      this.emitSelectionChange();
    },
    removeFolder(folderId) {
      const index = this.selectedFolderIds.indexOf(folderId);
      if (index !== -1) {
        this.selectedFolderIds.splice(index, 1);
      }
      this.loadSelectedFolders();
      this.emitSelectionChange();
    },
    emitSelectionChange() {
      const startWithColor = this.blockIndex % 2 === 1;
      const content = {
        type: 'gallery',
        folderIds: this.selectedFolderIds,
        galleryIds: this.selectedFolderIds,
        backgroundColor: this.companyColors.color3 || '',
        startWithColor,
      };
      this.$emit('selection-change', {
        hasSelection: this.selectedFolderIds.length > 0,
        selectionCount: this.selectedFolderIds.length,
        templateId: 'gallery-select',
        type: 'gallery',
        content: JSON.stringify(content),
        parsedContent: content,
      });
    },
    getGalleryBackground(galleryIndex) {
      if (this.isPopup || this.isEditMode) return 'transparent';
      const isBlockTransparent = this.blockBackground === 'transparent';
      if (galleryIndex % 2 === 0) {
        return this.blockBackground;
      }
      return isBlockTransparent ? (this.companyColors.color3 || 'transparent') : 'transparent';
    },
  },
};
</script>

<template>
  <div :class="b({ mobile: isMobile, popup: isPopup, preview: !isEditMode })">
    <div :class="b('header')" v-if="isPopup">
      <div :class="b('select-wrapper')">
        <div
          :class="b('select')"
          role="button"
          tabindex="0"
          @click="toggleDropdown"
          @keydown.enter="toggleDropdown"
        >
          <span :class="b('select-placeholder')">
            {{ selectedFolderIds.length > 0
              ? `${selectedFolderIds.length} selected`
              : 'Select Galleries' }}
          </span>
          <DropdownArrowIcon :class="b('select-arrow', { open: isDropdownOpen })" />
        </div>
        <div v-if="isDropdownOpen" :class="b('dropdown')">
          <div
            v-for="folder in folders"
            :key="folder.id"
            :class="b('dropdown-item')"
            role="button"
            tabindex="0"
            @click="toggleFolder(folder)"
            @keydown.enter="toggleFolder(folder)"
          >
            <span :class="b('dropdown-item-name', { selected: isSelected(folder.id) })">
              {{ folder.title }}
            </span>
            <CheckCircleIcon v-if="isSelected(folder.id)" :class="b('check-icon')" />
          </div>
          <div v-if="folders.length === 0" :class="b('dropdown-empty')">
            No galleries available
          </div>
        </div>
      </div>
      <div :class="b('selected-tags')">
        <div
          v-for="folder in selectedFolders"
          :key="folder.id"
          :class="b('tag')"
        >
          <span :class="b('tag-name')">{{ folder.title }}</span>
          <CloseOutlined :class="b('tag-remove')" @click="removeFolder(folder.id)" />
        </div>
      </div>
    </div>

    <div
      v-if="showItemsBlock && selectedFolders.length > 0"
      :class="b('content', { mobile: isMobile, popup: isPopup, preview: !isEditMode && !isPopup })"
    >
      <template v-if="selectedFolders.length > 0">
        <div
          v-for="(folder, folderIndex) in selectedFolders"
          :key="folder.id"
          :class="b('gallery-block', { mobile: isMobile, preview: !isEditMode && !isPopup })"
          :style="{ backgroundColor: getGalleryBackground(folderIndex) }"
        >
          <div :class="b('gallery-info', { mobile: isMobile })">
            <div :class="b('gallery-name', { mobile: isMobile })">{{ folder.title }}</div>
            <div :class="b('gallery-desc', { mobile: isMobile })">{{ folder.description }}</div>
          </div>
          <div :class="b('images-grid', { mobile: isMobile, popup: isPopup })">
            <div
              v-for="image in getFolderImages(folder)"
              :key="image.id"
              :class="b('image-card', { mobile: isMobile, popup: isPopup })"
            >
              <img
                :class="b('image')"
                :src="image.imageUrl"
                :alt="image.alt || image.title || 'Gallery image'"
              />
              <!-- Bottom gradient with title — hides on hover via CSS -->
              <div v-if="image.title" :class="b('image-gradient')">
                <div :class="b('image-gradient-title')">
                  {{ image.title }}
                </div>
              </div>
              <!-- Hover overlay with title + description + double chevron -->
              <div :class="b('image-overlay')">
                <div v-if="image.title" :class="b('image-overlay-title')">
                  {{ image.title }}
                </div>
                <div v-if="image.description" :class="b('image-overlay-description')">
                  {{ image.description }}
                </div>
                <ChevronDoubleRightIcon :class="b('image-overlay-chevron')" />
              </div>
            </div>
            <div
              v-if="getFolderImages(folder).length === 0"
              :class="b('empty-images')"
            >
              No images in this gallery
            </div>
          </div>
        </div>
      </template>
      <div v-else :class="b('empty-preview')">
        Select a gallery to see preview
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.gallery-select-module {
  width: 1540px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @include mediaMinMax(1025, $normal) {
    width: vw-full(1540);
    gap: vw-full(40);
  }

  &_popup {
    width: 1200px;
    gap: 24px;
    margin-bottom: 24px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1200);
      gap: vw-full(24);
      margin-bottom: vw-full(24);
    }
  }

  &_preview {
    width: 100%;
    padding: 0;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 40px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(40);
    }
  }

  &__select-wrapper {
    position: relative;
    width: 400px;
    height: 56px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(400);
      height: vw-full(56);
    }
  }

  &__select {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 20px;
    border: 1px solid #d9d9d9;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.2s;
    position: initial;
    z-index: 11;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(56);
      padding: vw-full(13) vw-full(20);
      border-radius: vw-full(12);
    }

    &:hover {
      border-color: #5f8aa4;
    }
  }

  &__select-placeholder {
    width: 100%;
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    color: #242424;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }
  }

  &__select-arrow {
    font-size: 10px;
    color: #7f7f7f;
    transition: transform 0.2s;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(10);
    }

    &_open {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 240px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
    padding-top: 10px;
    margin-top: -10px;

    @include mediaMinMax(1025, $normal) {
      max-height: vw-full(240);
      border-radius: vw-full(12);
    }
  }

  &__dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    cursor: pointer;
    transition: background 0.2s;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(10) vw-full(16);
    }

    &:hover {
      background: #f5f5f5;
    }
  }

  &__dropdown-item-name {
    font-family: Montserrat, sans-serif;
    font-size: 20px;
    color: #595959;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }

    &_selected {
      color: #293241;
      font-weight: 500;
    }
  }

  &__check-icon {
    color: #5f8aa4;
    font-size: 14px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(14);
    }
  }

  &__dropdown-empty {
    padding: 16px;
    text-align: center;
    color: #7f7f7f;
    font-family: Montserrat, sans-serif;
    font-size: 14px;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(16);
      font-size: vw-full(14);
    }
  }

  &__selected-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 20px;
    flex: 1;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8) vw-full(20);
    }
  }

  &__tag {
    width: 240px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 12px;
    border: 1px solid #d9d9d9;
    position: relative;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
      border-radius: vw-full(12);
    }
  }

  &__tag-name {
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    line-height: normal;
    color: #595959;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }

  &__tag-remove {
    position: absolute;
    top: 5px;
    right: 12px;
    font-size: 10px;
    line-height: 1;
    color: #595959;
    cursor: pointer;
    transition: color 0.2s;

    @include mediaMinMax(1025, $normal) {
      top: vw-full(5);
      right: vw-full(12);
      font-size: vw-full(10);
    }

    &:hover {
      color: #ff4d4f;
    }
  }

  &__content {
    min-height: 300px;
    background: transparent;
    display: flex;
    flex-direction: column;
    gap: 64px;

    @include mediaMinMax(1025, $normal) {
      min-height: vw-full(300);
      gap: vw-full(64);
    }

    &_preview {
      gap: 0;
    }
  }

  &__gallery-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 0;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(20);
    }

    &_popup {
      padding: 0;
    }

    &_preview {
      padding: 64px 0;

      @include mediaMinMax(1025, $normal) {
        padding: vw-full(64) 0;
      }
    }

    &_mobile {
      padding: 40px 0;
      gap: 16px;
    }
  }

  &__gallery-info {
    display: flex;
    flex-direction: column;
    gap: 20px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(20);
    }

    &_mobile {
      gap: 16px;
    }
  }

  &__gallery-name {
    font-family: Montserrat, sans-serif;
    color: #293241;
    text-align: center;
    font-size: 40px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: capitalize;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }

    &_popup {
      font-size: 28px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(28);
      }
    }

    &_mobile {
      font-size: 28px;
      font-weight: 500;
    }
  }

  &__gallery-desc {
    font-family: Montserrat, sans-serif;
    color: #5e5d5d;
    text-align: center;
    font-size: 19px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
    }

    &_mobile {
      font-size: 16px;
    }
  }

  &__images-grid {
    display: grid;
    grid-template-columns: repeat(3, 500px);
    gap: 20px;
    justify-content: center;

    @include mediaMinMax(1025, $normal) {
      grid-template-columns: repeat(3, vw-full(500));
      gap: vw-full(20);
    }

    &_popup {
      grid-template-columns: repeat(3, 370px);
      gap: 16px;

      @include mediaMinMax(1025, $normal) {
        grid-template-columns: repeat(3, vw-full(370));
        gap: vw-full(16);
      }
    }

    &_mobile {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  &__image-card {
    position: relative;
    border-radius: 20px;
    overflow: hidden;
    cursor: pointer;
    aspect-ratio: 1;

    @include mediaMinMax(1025, $normal) {
      border-radius: vw-full(20);
    }

    &_mobile {
      border-radius: 16px;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 20px;

    @include mediaMinMax(1025, $normal) {
      border-radius: vw-full(20);
    }
  }

  // Bottom gradient with title — visible by default, hides on hover
  &__image-gradient {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 180px;
    background: linear-gradient(to bottom, rgba(60, 60, 60, 0), #3C3C3C);
    border-radius: 0 0 20px 20px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 20px 40px;
    z-index: 1;
    transition: opacity 0.3s ease;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(180);
      border-radius: 0 0 vw-full(20) vw-full(20);
      padding: vw-full(20) vw-full(40);
    }
  }

  &__image-card:hover &__image-gradient {
    opacity: 0;
  }

  &__image-gradient-title {
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-weight: 500;
    font-size: 19px;
    text-transform: uppercase;
    line-height: normal;
    text-align: center;
    width: 100%;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
    }
  }

  // Hover overlay — slides up on card hover
  &__image-overlay {
    position: absolute;
    inset: 0;
    background: rgba(60, 60, 60, 0.8);
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 40px;
    z-index: 3;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.3s ease, transform 0.3s ease;

    @include mediaMinMax(1025, $normal) {
      border-radius: vw-full(20);
      padding: vw-full(40);
    }
  }

  &__image-card:hover &__image-overlay {
    opacity: 1;
    transform: translateY(0);
  }

  &__image-overlay-title {
    color: #fff;
    font-size: 32px;
    font-family: Montserrat, sans-serif;
    font-weight: 500;
    text-transform: uppercase;
    line-height: normal;
    margin-bottom: 20px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(32);
      margin-bottom: vw-full(20);
    }
  }

  &__image-overlay-description {
    color: #fff;
    font-size: 20px;
    font-family: Montserrat, sans-serif;
    font-weight: 400;
    line-height: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 7;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }
  }

  &__image-overlay-chevron {
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 28px;

    @include mediaMinMax(1025, $normal) {
      bottom: vw-full(40);
      width: vw-full(36);
      height: vw-full(28);
    }
  }

  // Popup mode — proportionally smaller overlay elements for 370px cards
  &__images-grid_popup &__image-gradient {
    height: 130px;
    padding: 14px 28px;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(130);
      padding: vw-full(14) vw-full(28);
    }
  }

  &__images-grid_popup &__image-gradient-title {
    font-size: 15px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(15);
    }
  }

  &__images-grid_popup &__image-overlay {
    padding: 28px;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(28);
    }
  }

  &__images-grid_popup &__image-overlay-title {
    font-size: 24px;
    margin-bottom: 14px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
      margin-bottom: vw-full(14);
    }
  }

  &__images-grid_popup &__image-overlay-description {
    font-size: 16px;
    -webkit-line-clamp: 5;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }

  &__images-grid_popup &__image-overlay-chevron {
    bottom: 28px;
    width: 28px;
    height: 22px;

    @include mediaMinMax(1025, $normal) {
      bottom: vw-full(28);
      width: vw-full(28);
      height: vw-full(22);
    }
  }

  &__empty-images,
  &__empty-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    color: #7f7f7f;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(200);
      font-size: vw-full(16);
    }
  }
}
</style>
